import { useContext, useState } from 'react';
import {
  Box,
  List,
  ListItem as MuiListItem,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Typography,
  useTheme,
  SvgIconTypeMap,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { PrivateLandOwnershipEntity } from 'shared/types';
import { MapEntityDrawerContext } from 'features/mapping/components/MapEntityDrawer';
import { MapEntityDrawerHeader } from 'features/mapping/components/MapEntityDrawerHeader';
import { getResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { getDisplayValueForKey, hasValidCoordinates } from 'shared/utils';
import { TabPanel, a11yProps } from 'components/TabPanel';
import { formatDate } from 'shared/dates';
import { parse } from 'date-fns';
import { DrawerStickyHeader } from 'components/DrawerStickyHeader';
import { InlineStatus } from 'components/InlineStatus';
import { EMPTY_DATA_VALUE } from '../../../../../constants';
import { usePrivateLandOwnership } from './usePrivateLandOwnership';

type ListItemProps = {
  label: string;
  primaryText: string;
  secondaryText?: string;
  Icon?: OverridableComponent<SvgIconTypeMap<object, 'svg'>> & {
    muiName: string;
  };
};

const TAB_PANEL_ID_PREFIX = 'land-ownership';

const ListItem = (props: ListItemProps): JSX.Element => {
  const { label, primaryText, secondaryText, Icon = LocationOnIcon } = props;
  const theme = useTheme();

  return (
    <MuiListItem divider>
      <ListItemText
        primary={
          <Typography
            color="secondary"
            variant="h4"
            sx={{
              fontWeight: 'bold',
              fontSize: getResponsiveFontSize(theme.typography.pxToRem(18)),
            }}
          >
            {label}
          </Typography>
        }
        secondary={
          <>
            <Typography
              color="textPrimary"
              sx={{
                fontWeight: 'medium',
              }}
            >
              {primaryText || EMPTY_DATA_VALUE}
            </Typography>
            {Boolean(secondaryText) && (
              <Typography color="textPrimary">{secondaryText}</Typography>
            )}
          </>
        }
        slotProps={{
          secondary: { component: 'div' },
        }}
      />
      <ListItemIcon sx={{ minWidth: 'fit-content', color: 'text.primary' }}>
        <Icon />
      </ListItemIcon>
    </MuiListItem>
  );
};

export const PrivateLandOwnershipContent = (
  props: PrivateLandOwnershipEntity,
): JSX.Element => {
  const { uuid } = props;
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useTranslation();
  const { scrollElementRef } = useContext(MapEntityDrawerContext);
  const { privateLandOwnership, isLoading, isError } = usePrivateLandOwnership({
    uuid,
  });

  const owner = getDisplayValueForKey(privateLandOwnership, 'owner');
  const address = getDisplayValueForKey(privateLandOwnership, 'address');
  const taxAddress = getDisplayValueForKey(privateLandOwnership, 'taxAddress');
  const coords = {
    lat: privateLandOwnership?.lat,
    lng: privateLandOwnership?.lng,
  };

  return (
    <Box>
      <DrawerStickyHeader
        scrollContainer={scrollElementRef?.current as HTMLElement}
      >
        <DrawerStickyHeader.ContentContainer>
          <MapEntityDrawerHeader>{owner}</MapEntityDrawerHeader>
          <Typography variant="body2" color="textSecondary">
            {address}
          </Typography>
        </DrawerStickyHeader.ContentContainer>
        <Tabs
          variant="fullWidth"
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="incident tabs"
          indicatorColor="primary"
          TabIndicatorProps={{ children: <span /> }}
        >
          <Tab
            label={t('privateLandOwnership.tabs.overview')}
            {...a11yProps(TAB_PANEL_ID_PREFIX, 0)}
          />
          <Tab
            label={t('privateLandOwnership.tabs.info')}
            {...a11yProps(TAB_PANEL_ID_PREFIX, 1)}
          />
        </Tabs>
      </DrawerStickyHeader>

      {(isLoading || isError || !privateLandOwnership) && (
        <InlineStatus status={isLoading ? 'loading' : 'error'} />
      )}

      <TabPanel value={activeTab} index={0} idPrefix={TAB_PANEL_ID_PREFIX}>
        {!isLoading && (
          <List>
            {address !== taxAddress && (
              <ListItem
                label={t('privateLandOwnership.overview.taxAddress')}
                primaryText={owner}
                secondaryText={taxAddress}
              />
            )}
            <ListItem
              label={t('privateLandOwnership.overview.coordinates')}
              primaryText={
                hasValidCoordinates(coords)
                  ? `${coords.lat}, ${coords.lng}`
                  : EMPTY_DATA_VALUE
              }
            />
            <ListItem
              label={t('privateLandOwnership.overview.acres')}
              primaryText={getDisplayValueForKey(
                privateLandOwnership,
                'acres',
                {
                  format: (value) => value.toFixed(1),
                },
              )}
            />
          </List>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={1} idPrefix={TAB_PANEL_ID_PREFIX}>
        {!isLoading && (
          <List>
            <ListItem
              label={t('privateLandOwnership.info.buildings')}
              primaryText={getDisplayValueForKey(
                privateLandOwnership,
                'buildings',
              )}
              Icon={LanguageIcon}
            />
            <ListItem
              label={t('privateLandOwnership.info.zoningType')}
              primaryText={getDisplayValueForKey(
                privateLandOwnership,
                'zoningType',
              )}
            />
            <ListItem
              label={t('privateLandOwnership.info.zoningSubtype')}
              primaryText={getDisplayValueForKey(
                privateLandOwnership,
                'zoningSubtype',
              )}
            />
            <ListItem
              label={t('privateLandOwnership.info.femaFloodZone')}
              primaryText={getDisplayValueForKey(
                privateLandOwnership,
                'femaFloodZone',
              )}
            />
            <ListItem
              label={t('privateLandOwnership.info.femaFloodZoneSubtype')}
              primaryText={getDisplayValueForKey(
                privateLandOwnership,
                'femaFloodZoneSubtype',
              )}
            />
            <ListItem
              label={t('privateLandOwnership.info.femaFloodZoneDate')}
              primaryText={getDisplayValueForKey(
                privateLandOwnership,
                'femaFloodZoneDataDate',
                {
                  format: (value) => {
                    const parsedDate = parse(
                      value,
                      'yyyy-MM-dd',
                      new Date(value),
                    );
                    return formatDate(parsedDate, 'MMMM d, yyyy');
                  },
                },
              )}
            />
          </List>
        )}
      </TabPanel>
    </Box>
  );
};
